import { useEffect, useRef, useState } from "react";
import { Ticket, Loader2, Sparkles, Share2, Gift } from "lucide-react";
import { ScratchWinShare } from "./ScratchWinShare";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { POINTS_EVENT } from "@/components/PointsBurst";
import { confetti, playCoin, playFanfare, playScratch, playDrumroll } from "@/lib/celebrate";

const errMsg = (e) => e?.response?.data?.detail || "Das hat nicht geklappt";

const SHARE_MIN = 50;

function ScratchCard({ ticketId, onDone }) {
  const { user } = useAuth();
  const [share, setShare] = useState(false);
  const canvas = useRef(null);
  const [prize, setPrize] = useState(null);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState(false);
  const started = useRef(false);
  const lastTick = useRef(0);

  useEffect(() => {
    const c = canvas.current, ctx = c.getContext("2d");
    const { width, height } = c.getBoundingClientRect();
    c.width = width * 2; c.height = height * 2; ctx.scale(2, 2);
    const g = ctx.createLinearGradient(0, 0, width, height);
    g.addColorStop(0, "#d4d4d8"); g.addColorStop(0.5, "#f4f4f5"); g.addColorStop(1, "#a1a1aa");
    ctx.fillStyle = g; ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = "#71717a"; ctx.font = "800 20px Outfit, sans-serif"; ctx.textAlign = "center";
    ctx.fillText("HIER RUBBELN ✨", width / 2, height / 2 + 7);
  }, []);

  const finish = (p) => {
    setDone(true);
    canvas.current.style.transition = "opacity .5s"; canvas.current.style.opacity = "0";
    window.dispatchEvent(new CustomEvent(POINTS_EVENT, { detail: { amount: p, reason: `Rubbellos: ${p} Punkte gewonnen 🎟️` } }));
    const r = canvas.current.getBoundingClientRect();
    if (p >= 25) { playDrumroll(500); setTimeout(() => { playFanfare(); [0, 400, 800].forEach((d, i) => setTimeout(() => confetti({ x: r.left + r.width * (0.2 + 0.3 * i), y: r.top + r.height / 2, count: 90, spread: 1.6 }), d)); }, 500); }
    else { playCoin(3); confetti({ x: r.left + r.width / 2, y: r.top + r.height / 2, count: 35, spread: 0.8 }); }
    onDone();
  };

  const scratch = (e) => {
    if (done || (e.buttons !== 1 && e.pointerType === "mouse")) return;
    const c = canvas.current, r = c.getBoundingClientRect(), ctx = c.getContext("2d");
    if (!started.current) {
      started.current = true; setTouched(true);
      api.post(`/scratch/${ticketId}/reveal`).then(({ data }) => setPrize(data.prize)).catch((er) => { toast.error(errMsg(er)); onDone(true); });
    }
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath(); ctx.arc(e.clientX - r.left, e.clientY - r.top, 22, 0, Math.PI * 2); ctx.fill();
    if (Date.now() - lastTick.current > 90) { lastTick.current = Date.now(); playScratch(); }
  };

  const check = () => {
    if (done || prize == null) return;
    const c = canvas.current, data = c.getContext("2d").getImageData(0, 0, c.width, c.height).data;
    let clear = 0;
    for (let i = 3; i < data.length; i += 64) if (data[i] === 0) clear++;
    if (clear / (data.length / 64) > 0.5) finish(prize);
  };

  useEffect(() => { if (prize != null && started.current) check(); }, [prize]); // eslint-disable-line react-hooks/exhaustive-deps

  return (<>
    <div className={`relative w-full max-w-sm mx-auto aspect-[16/9] rounded-3xl overflow-hidden border-4 border-amber-300 shadow-xl shadow-amber-100 select-none touch-none ${done && prize >= 25 ? "win-glow" : ""}`} data-testid="scratch-card">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-200 via-yellow-100 to-pink-200 flex flex-col items-center justify-center">
        {prize == null ? <Loader2 className="w-8 h-8 animate-spin text-amber-500" /> : <>
          <p className="text-xs font-extrabold uppercase tracking-widest text-amber-700">{prize >= 25 ? "Großer Gewinn! 🤩" : "Gewonnen"}</p>
          <p className={`font-display text-6xl font-black text-rose-600 ${done ? "prize-pop" : ""}`} data-testid="scratch-prize">+{prize}</p>
          <p className="text-sm font-bold text-amber-700">Punkte</p>
        </>}
      </div>
      <canvas ref={canvas} className="absolute inset-0 w-full h-full cursor-grab" data-testid="scratch-foil"
        onPointerDown={scratch} onPointerMove={scratch} onPointerUp={check} onPointerLeave={check} />
      {!touched && <span className="scratch-shine absolute inset-0 overflow-hidden pointer-events-none" aria-hidden />}
    </div>
    {done && prize >= SHARE_MIN && (
      <button type="button" onClick={() => setShare(true)} data-testid="scratch-win-share-trigger"
        className="prize-pop w-full max-w-sm mx-auto flex items-center justify-center gap-2 h-12 rounded-full bg-gradient-to-r from-amber-300 to-pink-400 text-rose-950 font-extrabold shadow-lg shadow-amber-200 active:scale-95 transition-transform">
        <Share2 className="w-5 h-5" /> Gewinn in Story teilen
      </button>
    )}
    {share && user && <ScratchWinShare prize={prize} user={user} onClose={() => setShare(false)} />}
  </>);
}

export function ScratchSection() {
  const { user, refreshUser } = useAuth();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [finished, setFinished] = useState(false);
  const load = () => api.get("/scratch").then(({ data }) => { setSt(data); if (!active && data.open[0]) setActive(data.open[0].id); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = async () => {
    const waiting = st.open.find((o) => o.id !== active);
    if (waiting) { setFinished(false); setActive(waiting.id); return; }
    setBusy(true);
    try { const { data } = await api.post("/scratch/buy"); setFinished(false); setActive(data.id); refreshUser(); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!st) return <div className="h-60 rounded-3xl bg-white animate-pulse" />;
  const left = st.daily_limit - st.bought_today;
  return (
    <section className="rounded-[2rem] bg-white border border-pink-100 p-5 sm:p-7 shadow-sm space-y-5" data-testid="scratch-section">
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-amber-300 to-pink-500 flex items-center justify-center"><Ticket className="w-6 h-6 text-white" /></span>
        <div>
          <h2 className="font-display text-xl font-extrabold">Rubbellose</h2>
          <p className="text-sm text-slate-500">Für {st.price} Punkte ein Los kaufen und bis zu <b className="text-rose-600">{st.max_prize} Punkte</b> freirubbeln!</p>
        </div>
      </div>
      {st.open.some((o) => o.free) && <p className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-50 to-pink-50 border border-amber-200 px-4 py-3 text-sm font-bold text-amber-700 fade-up" data-testid="scratch-free-hint"><Gift className="w-5 h-5 shrink-0" /> Willkommensgeschenk: Dein erstes Rubbellos ist gratis – rubbel los!</p>}
      {active ? <ScratchCard key={active} ticketId={active} onDone={(failed) => { if (failed) setActive(null); else setFinished(true); refreshUser(); load(); }} />
        : <div className="aspect-[16/9] max-w-sm mx-auto rounded-3xl border-2 border-dashed border-amber-200 bg-amber-50/50 flex flex-col items-center justify-center gap-1 text-center px-6">
            <Sparkles className="w-8 h-8 text-amber-400" />
            <p className="font-bold text-slate-700">Mögliche Gewinne</p>
            <p className="text-sm text-slate-500">{st.prizes.join(" · ")} Punkte</p>
          </div>}
      {(!active || finished) && <button type="button" onClick={buy} disabled={busy || left <= 0 || (user?.points ?? 0) < st.price} data-testid="scratch-buy-btn"
        className="w-full max-w-sm mx-auto flex items-center justify-center gap-2 h-14 rounded-full pink-gradient text-white font-extrabold shadow-lg shadow-rose-200 active:scale-95 transition-transform disabled:opacity-50">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Ticket className="w-5 h-5" />} {finished ? "Neues Los" : "Los kaufen"} · {st.price} Punkte
      </button>}
      <p className="text-center text-xs text-slate-400" data-testid="scratch-left">{left > 0 ? `Heute noch ${left} von ${st.daily_limit} Losen` : "Für heute alle Lose gekauft – morgen geht's weiter!"}{st.open.length > 1 ? ` · ${st.open.length - 1} weitere offen` : ""}</p>
    </section>
  );
}
